import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft,
  CheckCircle2,
  KeyRound,
  RefreshCw,
  Search,
  XCircle,
  X,
} from 'lucide-react'
import { useAuthStore } from '@/store'
import { fetchAuthRequestLog, type AuthRequestLogEntry } from '@/services/managementUsersService'

// Человеческие подписи к тому, что фактически лежит в auth_request_log.method
const METHOD_LABELS: Record<string, string> = {
  'email-request': 'Запрос ссылки на email',
  'email-verify': 'Переход по ссылке из письма',
  'link-email': 'Привязка email к аккаунту',
  telegram: 'Вход из Telegram',
  widget: 'Telegram-виджет в браузере',
  'telegram-bootstrap': 'Восстановление сессии в Telegram',
  refresh: 'Обновление токена',
  'email-legacy': 'Устаревший вход через Supabase Auth',
  unknown: 'Неизвестный метод',
}

// Расшифровка отказов: пользователю нужен смысл, а не текст ошибки функции
const ERROR_HINTS: Record<string, string> = {
  invite_required: 'Не зарегистрирован — нет ни привязанного email/Telegram, ни действующего инвайта',
  'Invalid email': 'Email введён в неверном формате',
  'Invalid email format': 'Email введён в неверном формате',
  'Email already in use': 'Этот email уже привязан к другому аккаунту',
  'Invalid or expired email token': 'Ссылка из письма просрочена или уже использована',
  'Invalid token': 'Токен недействителен — нужен повторный вход',
  'User not found': 'Пользователь не найден',
  'Failed to send login email': 'Письмо не ушло — сбой отправки',
}

const PERIOD_OPTIONS = [
  { value: 1, label: 'Сутки' },
  { value: 7, label: '7 дней' },
  { value: 30, label: '30 дней' },
  { value: 0, label: 'Всё' },
]

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function LogRow({ entry }: { entry: AuthRequestLogEntry }) {
  const isError = entry.outcome === 'error'
  const hint = entry.error_code ? ERROR_HINTS[entry.error_code] : null
  const who = entry.email || entry.user_display || (entry.telegram_id ? `tg:${entry.telegram_id}` : null)

  return (
    <div className="border-b border-border px-3 py-2.5 last:border-b-0">
      <div className="flex items-start gap-2.5">
        {isError ? (
          <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
        ) : (
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
        )}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-sm font-medium text-foreground">
              {METHOD_LABELS[entry.method] ?? entry.method}
            </span>
            <span className="text-xs text-muted-foreground">{formatDateTime(entry.created_at)}</span>
            <span
              className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                isError ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'
              }`}
            >
              {entry.status_code}
            </span>
          </div>

          {who && <div className="mt-0.5 truncate text-xs text-foreground">{who}</div>}

          {entry.error_code && (
            <div className="mt-0.5 text-xs text-red-600">
              {hint ?? entry.error_code}
            </div>
          )}

          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
            {entry.invite_token_present && <span>с инвайтом</span>}
            {entry.ip && <span>IP {entry.ip}</span>}
            {entry.duration_ms !== null && <span>{entry.duration_ms} мс</span>}
            {entry.function_name !== 'auth' && <span>{entry.function_name}</span>}
          </div>
        </div>
      </div>
    </div>
  )
}

export function ManagementUsersAuthLogPage() {
  const navigate = useNavigate()
  const user = useAuthStore(s => s.user)
  const [search, setSearch] = useState('')
  const [onlyErrors, setOnlyErrors] = useState(false)
  const [days, setDays] = useState(7)

  const since = useMemo(
    () => (days > 0 ? new Date(Date.now() - days * 86400000).toISOString() : null),
    [days]
  )

  const { data: entries = [], isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['auth-request-log', days, onlyErrors],
    queryFn: () => fetchAuthRequestLog({ limit: 500, onlyErrors, since }),
    enabled: !!user?.id,
    staleTime: 60 * 1000,
  })

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return entries as AuthRequestLogEntry[]
    return (entries as AuthRequestLogEntry[]).filter(entry =>
      [
        entry.email ?? '',
        entry.user_display ?? '',
        entry.method,
        entry.error_code ?? '',
        entry.ip ?? '',
        String(entry.telegram_id ?? ''),
      ]
        .join(' ')
        .toLowerCase()
        .includes(term)
    )
  }, [entries, search])

  const stats = useMemo(() => {
    const list = filtered
    const errors = list.filter(e => e.outcome === 'error').length
    const notRegistered = list.filter(e => e.error_code === 'invite_required').length
    return { total: list.length, errors, notRegistered }
  }, [filtered])

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <div className="mx-auto max-w-5xl px-4 py-5">
        <button
          type="button"
          onClick={() => navigate('/management/users')}
          className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Пользователи
        </button>

        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" />
              <h1 className="text-xl font-bold text-foreground">Попытки авторизации</h1>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Каждое обращение к входу — успешное и отказное. Отказ «не зарегистрирован» виден сразу.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void refetch()}
            className="inline-flex h-9 items-center gap-1.5 self-start rounded-md border border-border bg-card px-3 text-xs font-medium text-muted-foreground hover:bg-muted"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            Обновить
          </button>
        </div>

        <div className="rounded-md border border-border bg-card">
          <div className="border-b border-border p-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <input
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Поиск по email, имени, IP, методу"
                className="h-9 w-full rounded-md border border-border bg-background pl-8 pr-8 text-sm outline-none focus:border-slate-400"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted"
                  title="Очистить поиск"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              {PERIOD_OPTIONS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setDays(option.value)}
                  className={`h-8 shrink-0 rounded-md border px-2.5 text-xs font-medium ${
                    days === option.value
                      ? 'border-foreground bg-foreground text-background'
                      : 'border-border bg-background text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {option.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setOnlyErrors(value => !value)}
                className={`h-8 shrink-0 rounded-md border px-2.5 text-xs font-medium ${
                  onlyErrors
                    ? 'border-fixed-red-600 bg-fixed-red-600 text-white'
                    : 'border-border bg-background text-muted-foreground hover:bg-muted'
                }`}
              >
                Только отказы
              </button>
            </div>

            {!isLoading && !error && (
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                <span>Записей: {stats.total}</span>
                <span>Отказов: {stats.errors}</span>
                <span>Из них «не зарегистрирован»: {stats.notRegistered}</span>
              </div>
            )}
          </div>

          {isLoading && <div className="p-6 text-center text-sm text-muted-foreground">Загружаем журнал…</div>}

          {error && (
            <div className="p-6 text-center text-sm text-red-600">
              Не удалось загрузить журнал. Нужен доступ advanced в УК и применённая миграция
              <code className="mx-1 rounded bg-red-50 px-1">auth_request_log</code>.
            </div>
          )}

          {!isLoading && !error && filtered.length === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground">
              За выбранный период записей нет. Журнал наполняется с момента деплоя auth-функций.
            </div>
          )}

          {!isLoading && !error && filtered.map(entry => <LogRow key={entry.id} entry={entry} />)}
        </div>
      </div>
    </div>
  )
}
